import { useState } from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { Card, IconButton, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { PageTitle } from '@/components/shell/PageTitle';
import { PillTabs } from '@/components/shell/PillTabs';
import { SectionHeader } from '@/components/shell/SectionHeader';
import { StatusChip } from '@/components/shell/StatusChip';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';
import { tenantStatusTone } from './domain';
import { TenantGeneralPanel } from './TenantGeneralPanel';
import { TenantPolicyPanel } from './TenantPolicyPanel';

/**
 * Tenant detail against `GET /v1/admin/tenants/{code}` (A2's contract). The
 * general/policy split is a `PillTabs` switch under one `SectionHeader`,
 * mirroring the operations surface (B2) rather than a second route, since
 * both sub-views share the same tenant identity and refresh affordance.
 */
export function TenantDetailPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const tenantCode = code ?? '';
  const [tab, setTab] = useState<'general' | 'policy'>('general');
  const { data, loading, error, reload } = useApiResource(() => payApiClient.getAdminTenant(tenantCode), tenantCode);

  const backButton = (
    <IconButton onClick={() => navigate('/tenants')} aria-label={translate('pay.tenants.detail.back')} size="small">
      <ArrowBackOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          <LoadingState variant="card" />
        </Card>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {backButton}
        </Stack>
        <Card>
          {error.errorCode === 'access_denied' ? (
            <AccessDenied />
          ) : error.errorCode === 'not_found' ? (
            <EmptyState
              title={translate('pay.tenants.detail.notFoundTitle')}
              body={translate('pay.tenants.detail.notFoundBody')}
            />
          ) : (
            <ErrorState body={error.message} onRetry={reload} />
          )}
        </Card>
      </Stack>
    );
  }

  if (!data) return null;

  function handleChanged() {
    reload();
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {backButton}
        <PageTitle
          title={data.name}
          subtitle={data.code}
          adornment={<StatusChip label={translate(`pay.tenants.status.${data.status}`)} tone={tenantStatusTone(data.status)} />}
        />
      </Stack>

      <SectionHeader
        left={
          <PillTabs
            value={tab}
            onChange={(next) => setTab(next as 'general' | 'policy')}
            options={[
              { value: 'general', label: translate('pay.tenants.detail.tabsGeneral') },
              { value: 'policy', label: translate('pay.tenants.detail.tabsPolicy') },
            ]}
          />
        }
        onRefresh={reload}
      />

      {tab === 'general' ? (
        <TenantGeneralPanel tenant={data} onChanged={handleChanged} />
      ) : (
        <TenantPolicyPanel tenantCode={data.code} />
      )}
    </Stack>
  );
}
